import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listNotifications,
  markRead,
  markAllRead,
  type DriverNotificationDTO,
} from '../../lib/driver-notifications-api';
import {
  listCachedPushNotifications,
  type CachedPushNotification,
} from '../../lib/push-cache';
import { EmptyState } from '../../components/kit/EmptyState';
import { Spinner } from '../../components/kit/Spinner';
import { haptic } from '../../lib/haptic';
import { formatISTDateTime } from '../../lib/format';

type Row = {
  id: string;
  title: string;
  body: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
};

function fromServer(d: DriverNotificationDTO): Row {
  return {
    id: d.id,
    title: d.title,
    body: d.body,
    link: d.link,
    readAt: d.readAt,
    createdAt: d.createdAt,
  };
}

function fromCache(c: CachedPushNotification): Row {
  return {
    id: c.id,
    title: c.title,
    body: c.body,
    link: c.link ?? null,
    readAt: c.readAt ? new Date(c.readAt).toISOString() : null,
    createdAt: new Date(c.receivedAt).toISOString(),
  };
}

export function NotificationsRoute() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setOffline(false);
    try {
      const result = await listNotifications();
      setRows(result.items.map(fromServer));
      setNextCursor(result.nextCursor);
    } catch {
      // Fallback to IDB
      setOffline(true);
      const cached = await listCachedPushNotifications();
      setRows(cached.map(fromCache));
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const handleTap = async (row: Row) => {
    haptic.tap();
    if (!row.readAt) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, readAt: new Date().toISOString() } : r)));
      void markRead([row.id]); // optimistic; fire-and-forget
    }
    if (row.link) navigate(row.link);
  };

  const handleMarkAllRead = async () => {
    haptic.tap();
    setRows((prev) => prev.map((r) => (r.readAt ? r : { ...r, readAt: new Date().toISOString() })));
    void markAllRead();
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await listNotifications(nextCursor);
      setRows((prev) => [...prev, ...result.items.map(fromServer)]);
      setNextCursor(result.nextCursor);
    } catch {
      // silent — user can pull-to-refresh
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size={32} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-5 pt-6 pb-10">
        <h1
          className="text-base font-bold mb-4 text-center"
          style={{ color: 'var(--color-text)', letterSpacing: '-0.01em' }}
        >
          {t('notifications.title')}
        </h1>
        <EmptyState title={t('notifications.empty')} />
      </div>
    );
  }

  const hasUnread = rows.some((r) => !r.readAt);

  return (
    <div className="px-5 pt-4 pb-10">
      <div className="flex items-center justify-between mb-4">
        <h1
          className="text-base font-bold"
          style={{ color: 'var(--color-text)', letterSpacing: '-0.01em' }}
        >
          {t('notifications.title')}
        </h1>
        {hasUnread && (
          <button
            type="button"
            onClick={handleMarkAllRead}
            className="text-xs font-medium text-[var(--green-700,#1B6A41)]"
          >
            {t('notifications.mark_all_read', { defaultValue: 'Mark all read' })}
          </button>
        )}
      </div>

      {offline && (
        <div className="mb-3 rounded-lg bg-[var(--status-warning-bg)] border border-[var(--color-border)] px-3 py-2 text-xs text-[var(--status-warning-fg)]">
          {t('notifications.offline_banner', {
            defaultValue: 'Showing cached notifications — try again later',
          })}
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            onClick={() => void handleTap(row)}
            className={`cursor-pointer rounded-2xl border border-[var(--color-border,#d1d5db)] px-4 py-3 ${
              // Read cards use the themed surface (white in light, #1a2028 in
              // dark) — NOT hardcoded bg-white, which left the themed --color-text
              // as light-on-white (invisible) in dark mode. Unread keeps the
              // dark-aware --mint-soft tint.
              row.readAt ? 'bg-[var(--color-surface)]' : 'bg-[var(--mint-soft,#f0f8f4)]'
            }`}
          >
            <div className="text-sm font-medium text-[var(--color-text)]">{row.title}</div>
            <div className="text-xs text-[var(--color-text-secondary)] mt-1">{row.body}</div>
            <div className="text-[10px] text-[var(--color-text-secondary)] mt-1">
              {formatISTDateTime(row.createdAt)}
            </div>
          </li>
        ))}
      </ul>

      {nextCursor && (
        <button
          type="button"
          onClick={() => void handleLoadMore()}
          disabled={loadingMore}
          className="mt-4 w-full rounded-full border border-[var(--color-border,#d1d5db)] py-2 text-sm"
        >
          {loadingMore ? <Spinner size={16} /> : t('notifications.load_more', { defaultValue: 'Load more' })}
        </button>
      )}
    </div>
  );
}
